import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import pettyCashService from '../services/pettyCashService';
import s3Service from '../services/s3Service';
import styles from '../assets/pettyCashCreate.module.css';

const PettyCashCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expenses, setExpenses] = useState([
    {
      user_id: '',
      date: '',
      amount: '',
      description: '',
      file: null,
      fileKey: null
    }
  ]);
  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState({}); // Changed to object to index by expense index
  const [showTypeahead, setShowTypeahead] = useState({});
  const [showDatePicker, setShowDatePicker] = useState({});
  const typeaheadRefs = useRef({});
  const datePickerRefs = useRef({});

  useEffect(() => {
    loadEmployees();
  }, []);

  // Click outside handler for typeahead dropdowns and date pickers
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close typeahead dropdowns
      Object.keys(showTypeahead).forEach(index => {
        if (showTypeahead[index] && typeaheadRefs.current[index] && !typeaheadRefs.current[index].contains(event.target)) {
          setShowTypeahead(prev => ({ ...prev, [index]: false }));
        }
      });
      
      // Close date pickers
      Object.keys(showDatePicker).forEach(index => {
        if (showDatePicker[index] && datePickerRefs.current[index] && !datePickerRefs.current[index].contains(event.target)) {
          setShowDatePicker(prev => ({ ...prev, [index]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTypeahead, showDatePicker]);

  const loadEmployees = async () => {
    try {
      const response = await pettyCashService.getEmployees();
      if (response.success) {
        // Transform string array to object array if needed
        const employeesData = response.data.map((emp, index) => {
          if (typeof emp === 'string') {
            // If employee is a string, convert to object
            return {
              id: index + 1,
              name: emp,
              email: `${emp.toLowerCase().replace(/\s+/g, '.')}@example.com`
            };
          }
          return emp; // Already an object
        });
        setEmployees(employeesData);
        console.log('✅ Employees loaded:', employeesData.length, employeesData);
      } else {
        // Mock data for development
        setEmployees([
          { id: 1, name: 'John Doe', email: 'john@example.com' },
          { id: 2, name: 'Jane Smith', email: 'jane@example.com' },
          { id: 3, name: 'Mike Johnson', email: 'mike@example.com' },
          { id: 4, name: 'Sarah Wilson', email: 'sarah@example.com' },
          { id: 5, name: 'David Brown', email: 'david@example.com' }
        ]);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      // Mock data for development
      setEmployees([
        { id: 1, name: 'John Doe', email: 'john@example.com' },
        { id: 2, name: 'Jane Smith', email: 'jane@example.com' },
        { id: 3, name: 'Mike Johnson', email: 'mike@example.com' },
        { id: 4, name: 'Sarah Wilson', email: 'sarah@example.com' },
        { id: 5, name: 'David Brown', email: 'david@example.com' }
      ]);
    }
  };

  const handleInputChange = (index, field, value) => {
    const newExpenses = [...expenses];
    
    // Handle typeahead for employee search - special handling for user_id field
    if (field === 'user_id') {
      // When user types, update employee_name for display and clear user_id (will be set when employee is selected)
      newExpenses[index] = {
        ...newExpenses[index],
        employee_name: value,
        user_id: '' // Clear user_id until employee is selected from dropdown
      };
      
      if (value.length > 0) {
        const filtered = employees.filter(emp => 
          (emp.name && emp.name.toLowerCase().includes(value.toLowerCase())) ||
          (emp.email && emp.email.toLowerCase().includes(value.toLowerCase()))
        );
        console.log('🔍 Employee search:', { 
          value, 
          employeesCount: employees.length, 
          filteredCount: filtered.length, 
          filtered: filtered.map(e => e.name),
          allEmployees: employees.map(e => e.name)
        });
        setFilteredEmployees(prev => ({ ...prev, [index]: filtered })); // Index by expense index
        setShowTypeahead(prev => ({ ...prev, [index]: true }));
        console.log('✅ Typeahead state updated:', { index, showTypeahead: true, filteredCount: filtered.length });
      } else {
        setFilteredEmployees(prev => ({ ...prev, [index]: [] })); // Clear filtered employees for this index
        setShowTypeahead(prev => ({ ...prev, [index]: false }));
      }
    } else {
      // For other fields, update normally
      newExpenses[index] = {
        ...newExpenses[index],
        [field]: value
      };
    }
    
    setExpenses(newExpenses);
    
    // Clear error when user starts typing
    if (errors[`${index}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${index}.${field}`]: ''
      }));
    }
  };

  const selectEmployee = (index, employee) => {
    const newExpenses = [...expenses];
    newExpenses[index] = {
      ...newExpenses[index],
      user_id: employee.id,
      employee_name: employee.name
    };
    setExpenses(newExpenses);
    setShowTypeahead(prev => ({ ...prev, [index]: false }));
    setFilteredEmployees(prev => ({ ...prev, [index]: [] })); // Clear filtered employees after selection
  };

  const handleDateClick = (index) => {
    setShowDatePicker(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const selectDate = (index, day, month, year) => {
    const formattedDate = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    const newExpenses = [...expenses];
    newExpenses[index] = {
      ...newExpenses[index],
      date: formattedDate
    };
    setExpenses(newExpenses);
    setShowDatePicker(prev => ({ ...prev, [index]: false }));
  };

  const handleFileChange = async (index, file) => {
    try {
      // Upload file to S3 (matching Laravel path: public/petty_cash/files/)
      // useLaravelStructure: true ensures no timestamp prefix (matches Laravel's getDirectoryShort())
      const fileKey = await s3Service.uploadDocument(file, { 
        folder: 'public/petty_cash/files/',
        useLaravelStructure: true 
      });
      
      const newExpenses = [...expenses];
      newExpenses[index] = {
        ...newExpenses[index],
        file: file,
        fileKey: fileKey
      };
      setExpenses(newExpenses);
      
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  const addExpense = () => {
    setExpenses([
      ...expenses,
      {
        user_id: '',
        date: '',
        amount: '',
        description: '',
        file: null,
        fileKey: null
      }
    ]);
  };

  const removeExpense = (index) => {
    console.log('Removing expense at index:', index);
    console.log('Current expenses:', expenses);
    if (expenses.length > 1) {
      const newExpenses = expenses.filter((_, i) => i !== index);
      console.log('New expenses after removal:', newExpenses);
      setExpenses(newExpenses);
    } else {
      console.log('Cannot remove - only one expense left');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    expenses.forEach((expense, index) => {
      if (!expense.user_id) {
        newErrors[`${index}.user_id`] = 'Employee name is required';
      }
      if (!expense.date) {
        newErrors[`${index}.date`] = 'Date is required';
      } else {
        // Validate date format (dd/mm/yyyy)
        const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!dateRegex.test(expense.date)) {
          newErrors[`${index}.date`] = 'Date must be in dd/mm/yyyy format';
        }
      }
      if (!expense.amount) {
        newErrors[`${index}.amount`] = 'Amount is required';
      } else if (isNaN(parseFloat(expense.amount))) {
        newErrors[`${index}.amount`] = 'Amount must be a number';
      } else if (parseFloat(expense.amount) <= 0) {
        newErrors[`${index}.amount`] = 'Amount must be greater than 0';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    try {
      setSubmitting(true);
      
      const formData = new FormData();
      expenses.forEach((expense, index) => {
        formData.append(`pettycash[${index}][user_id]`, expense.user_id);
        formData.append(`pettycash[${index}][date]`, expense.date); // Already in dd/mm/yyyy format
        formData.append(`pettycash[${index}][amount]`, expense.amount);
        formData.append(`pettycash[${index}][description]`, expense.description || '');
        // Only send fileKey if file was uploaded to S3 (file is already uploaded, no need to send file object)
        if (expense.fileKey) {
          formData.append(`pettycash[${index}][fileKey]`, expense.fileKey);
        }
        // Note: We don't send expense.file because it's already uploaded to S3 via s3Service.uploadDocument()
      });
      
      const response = await pettyCashService.createExpenses(formData);
      
      if (response.success) {
        toast.success(response.message || "Expenses created successfully!");
        navigate('/petty-cash', { state: { refresh: true } });
      } else {
        toast.error(response.message || "Failed to create expenses");
      }
    } catch (error) {
      console.error('Error creating expenses:', error);
      toast.error("Failed to create expenses");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/petty-cash');
  };

  // Generate calendar days for date picker
  const generateCalendarDays = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    
    const days = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    
    return { 
      days, 
      monthName: monthNames[currentMonth], 
      year: currentYear 
    };
  };

  const { days, monthName, year } = generateCalendarDays();

  return (
    <div className={styles.container} id="js-add-petty-cash">
      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumbWrapper}>
        <ol className={styles.breadcrumb}>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate('/dashboard')} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={styles.breadcrumbItem}>
            <span onClick={() => navigate('/petty-cash')} className={styles.breadcrumbLink} style={{ cursor: 'pointer', color: '#337ab7' }}>Petty Cash</span>
          </li>
          <li className={styles.breadcrumbSeparator}>/</li>
          <li className={`${styles.breadcrumbItem} ${styles.breadcrumbActive}`}>Add expenses</li>
        </ol>
      </div>

      <div className={styles.row}>
        <div className={styles.colXs12}>
          <div className={styles.panelTitle}>
            <h3 className={styles.panelTitleText}>Add expenses</h3>
          </div>
          <div className={styles.panelGroup}>
            <form onSubmit={handleSubmit} className={styles.pettyCashForm}>
              <div className={styles.addManyExpensesWrapper} data-index="0">
                <div className={styles.addButtonWrapper}>
                  <button 
                    type="button" 
                    className={`${styles.btn} ${styles.btnExpense} ${styles.btnAddNew}`}
                    onClick={addExpense}
                  >
                    <i className="glyphicon glyphicon-plus"></i> Add
                  </button>
                </div>

                {expenses.map((expense, index) => (
                  <div key={index} style={{marginBottom: '20px'}}>
                    {/* Typeahead Container */}
                    <div className={styles.jsPettycashTypeahead}></div>

                    <div className={styles.expenseToAdd}>
                      <button 
                        type="button" 
                        className={`${styles.btn} ${styles.btnRemove}`}
                        onClick={() => removeExpense(index)}
                        disabled={expenses.length === 1}
                        title="Remove expense"
                      >
                        {/* <i className="glyphicon glyphicon-remove"></i> */}
                        x
                      </button>
                      
                      <div className={styles.row}>
                        <div className={styles.colSm6}>
                          <div className={`${styles.formGroup} ${errors[`${index}.user_id`] ? styles.hasError : ''}`}>
                            <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>
                              Employee Name
                            </label>
                            <div className={styles.colSm7}>
                              <div className={styles.typeaheadContainer} ref={el => typeaheadRefs.current[index] = el}>
                                <input
                                  type="text"
                                  className={`${styles.formControl} ${styles.typeahead}`}
                                  value={expense.employee_name || ''}
                                  onChange={(e) => handleInputChange(index, 'user_id', e.target.value)}
                                  placeholder="Search employee..."
                                />
                                {showTypeahead[index] && filteredEmployees[index] && filteredEmployees[index].length > 0 && (
                                  <div className={styles.typeaheadDropdown}>
                                    {filteredEmployees[index].map((employee, empIndex) => (
                                      <div
                                        key={employee.id || empIndex}
                                        className={styles.typeaheadItem}
                                        onClick={() => selectEmployee(index, employee)}
                                      >
                                        {employee.name} {employee.email ? `(${employee.email})` : ''}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {errors[`${index}.user_id`] && (
                                <span className={styles.helpBlock}>{errors[`${index}.user_id`]}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={styles.colSm6}>
                          <div className={styles.formGroup}>
                            <label className={`${styles.controlLabel} ${styles.colSm5}`}>
                              File
                            </label>
                            <div className={styles.colSm7}>
                              <div className={styles.inputImage}>
                                <input
                                  type="file"
                                  className={styles.formControl}
                                  style={{height: 'auto'}}
                                  onChange={(e) => handleFileChange(index, e.target.files[0])}
                                  accept=".pdf"
                                />
                              </div>
                            </div>
                          </div>
                          <div className={styles.clearfix}></div>
                        </div>
                      </div>
                      
                      <div className={styles.row}>
                        <div className={styles.colSm6}>
                          <div className={`${styles.formGroup} ${errors[`${index}.date`] ? styles.hasError : ''}`}>
                            <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>
                              Date
                            </label>
                            <div className={styles.colSm7}>
                              <div className={styles.datePickerContainer} ref={el => datePickerRefs.current[index] = el}>
                                <input
                                  type="text"
                                  className={`${styles.formControl} ${styles.jsDatepicker}`}
                                  value={expense.date}
                                  onChange={(e) => handleInputChange(index, 'date', e.target.value)}
                                  onClick={() => handleDateClick(index)}
                                  placeholder="dd/mm/yyyy"
                                  readOnly
                                  required
                                />
                                {showDatePicker[index] && (
                                  <div className={styles.datePickerDropdown}>
                                    <div className={styles.datePickerHeader}>
                                      <span>{monthName} {year}</span>
                                    </div>
                                    <div className={styles.datePickerGrid}>
                                      <div className={styles.datePickerWeekdays}>
                                        <span>Sun</span>
                                        <span>Mon</span>
                                        <span>Tue</span>
                                        <span>Wed</span>
                                        <span>Thu</span>
                                        <span>Fri</span>
                                        <span>Sat</span>
                                      </div>
                                      <div className={styles.datePickerDays}>
                                        {days.map((day, dayIndex) => (
                                          <span
                                            key={dayIndex}
                                            className={`${styles.datePickerDay} ${day ? styles.available : styles.empty}`}
                                            onClick={() => day && selectDate(index, day, new Date().getMonth() + 1, year)}
                                          >
                                            {day}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              {errors[`${index}.date`] && (
                                <span className={styles.helpBlock}>{errors[`${index}.date`]}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={styles.colSm6}>
                          <div className={`${styles.formGroup} ${errors[`${index}.amount`] ? styles.hasError : ''}`}>
                            <label className={`${styles.controlLabel} ${styles.colSm5} ${styles.labelRequired}`}>
                              Amount
                            </label>
                            <div className={styles.colSm7}>
                              <input
                                type="text"
                                className={styles.formControl}
                                value={expense.amount}
                                onChange={(e) => handleInputChange(index, 'amount', e.target.value)}
                                placeholder="0.00"
                                required
                              />
                              {errors[`${index}.amount`] && (
                                <span className={styles.helpBlock}>{errors[`${index}.amount`]}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className={styles.row}>
                        <div className={styles.colSm6}>
                          <div className={styles.formGroup}>
                            <label className={`${styles.controlLabel} ${styles.colSm3}`}>
                              Description
                            </label>
                            <div className={styles.colSm9}>
                              <textarea
                                className={styles.formControl}
                                value={expense.description}
                                onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                                placeholder="Enter description..."
                                rows="3"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className={`${styles.formGroup} ${styles.textRight} ${styles.mt20}`} style={{display: 'flex', justifyContent: 'flex-end'}}>
                  <button
                    type="submit"
                    className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PettyCashCreate; 